/**
 * OWNER-ONLY, CLIENT-ONLY recent-activity markers for personal priorities.
 *
 * WHY THIS EXISTS (link-activity ordering): Home/My priorities sorts active
 * items by "most recently touched" using personalPriorities.updatedAt. But
 * LINKING or UNLINKING a personal priority to/from a team direction does NOT
 * change that row's updatedAt — the link lives in a separate table
 * (personalPriorityLinks), and the owner-only mineForWeek read intentionally
 * does NOT expose link timestamps (its output shape is contract-pinned to
 * {personalPriorityId, teamPriorityId, teamPriorityTitle, teamId} — no
 * createdAt). So a link/unlink was invisible to Home's ordering.
 *
 * This module records "the owner just linked/unlinked item X" as a local,
 * in-memory timestamp so Home's sort can treat that as recent activity and
 * float the item near the top — exactly where the user expects to find the
 * thing they just re-organized.
 *
 * PRIVACY / SAFETY (all deliberate):
 *   - CLIENT-ONLY: a plain in-process Map. Never sent to the server, never
 *     persisted (no localStorage/sessionStorage), no schema, no endpoint.
 *   - OWNER-ONLY: it only ever holds ids the CURRENT session's user acted on;
 *     there is no other user's data to hold.
 *   - NOT A SIGNAL: it produces no count, no aggregate, no Team Pulse input,
 *     no dashboard/statistics/participation signal. It only influences the
 *     order of the owner's own list on their own Home.
 *   - BOUNDED + self-clearing: it grows only by the handful of items the user
 *     links/unlinks in a session and is wiped on any full page reload
 *     (module re-initialisation) — the "session lifecycle" clearing the task
 *     asked for. Nothing to garbage-collect on the server side.
 */
const activity = new Map<number, number>();

/**
 * Record recent link/unlink activity for one personal priority. `at` defaults
 * to now; callers in tests pass an explicit timestamp for determinism.
 */
export function markPersonalPriorityActivity(
  id: number,
  at: number = Date.now(),
): void {
  if (!Number.isFinite(id)) return;
  activity.set(id, at);
}

/** The recent-activity timestamp for one personal priority, or undefined. */
export function personalPriorityActivityAt(id: number): number | undefined {
  return activity.get(id);
}

/** Test-only: reset the in-memory store between cases. */
export function __resetPersonalPriorityActivity(): void {
  activity.clear();
}
